import { IssueComment } from '@mesa/core/comments';
import type { ReviewRecord } from '@mesa/core/review';
import { schema } from '@mesa/core/zero';
import { Row } from '@rocicorp/zero';
import { useQuery as useZeroQuery } from '@rocicorp/zero/react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { Editor } from '@tiptap/react';
import {
  MessageSquareDashedIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
  AlertTriangleIcon,
  RefreshCwIcon,
} from 'lucide-react';
import { memo, Suspense, useRef, useState, useMemo } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { ulid } from 'ulid';

import { DiscussionCommentEditor } from '@/components/editor/discussion-comment-editor';
import { MarkdownEditor } from '@/components/editor/markdown-editor';
import { ReactionBar } from '@/components/reactions/reaction-bar';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ThumbsDownIcon } from '@/components/ui/thumbs-down-icon';
import { ThumbsUpIcon } from '@/components/ui/thumbs-up-icon';
import { getTimeAgo } from '@/lib/time';
import { cn } from '@/lib/utils';
import { useZero } from '@/lib/zero';
import { collaboratorsQuery } from '@/services/repo';
import { useUser } from '@/services/user';

// Types
type Author = {
  id: number;
  login: string;
  avatarUrl: string;
};

type NormalizedComment = {
  id: number | null;
  gid: string;
  author: Author;
  body: string;
  createdAt: string;
  updatedAt: string;
  type: 'issue_comment' | 'review';
  state?: string;
  reactions?: any[];
};

type ReviewRecordRow = Row<typeof schema.tables.ReviewTable>;

// Custom hook for pull discussion data
function usePullDiscussionData() {
  const { owner, repo, number } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { user } = useUser();
  const z = useZero();

  // Fetch issue comments and reviews using Zero
  const [issueCommentsRaw] = useZeroQuery(
    z.query.IssueCommentsTable.where(({ and, cmp }) =>
      and(cmp('owner', owner), cmp('repo', repo), cmp('issueNumber', Number(number)))
    )
  );

  const [reviews] = useZeroQuery(
    z.query.ReviewTable.where(({ and, cmp }) =>
      and(cmp('owner', owner), cmp('repo', repo), cmp('pullNumber', Number(number)))
    )
  );

  // Normalize and combine comments
  const discussionComments = useMemo(() => {
    if (!issueCommentsRaw || !reviews) return [];

    const normalizedIssueComments: NormalizedComment[] = issueCommentsRaw.map(normalizeIssueComment).filter(Boolean);

    const normalizedReviews: NormalizedComment[] = reviews.map(normalizeReview).filter((c) => !!c);

    return [...normalizedIssueComments, ...normalizedReviews].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
  }, [issueCommentsRaw, reviews]);

  // Create issue comment mutation
  const createIssueComment = async (body: string) => {
    z.mutate.IssueCommentsTable.insert({
      gid: ulid(),
      author: {
        login: user.login,
        id: user.id,
        avatarUrl: user.avatar_url,
      },
      authorLogin: user.login,
      owner,
      repo,
      issueNumber: Number(number),
      body,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
  };

  return { discussionComments, createIssueComment, isLoading: !issueCommentsRaw || !reviews };
}

// Component-specific loading skeleton
function PullDiscussionSkeleton() {
  return (
    <div className="w-full py-6">
      <div className="px-4">
        <Separator className="w-full mb-6" />
        <div className="max-w-4xl mx-auto">
          <div className="border border-stone-200 rounded-lg bg-white">
            {/* Header skeleton */}
            <div className="flex items-center justify-between p-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-4 w-4" />
                <Skeleton className="h-5 w-20" />
              </div>
              <Skeleton className="h-4 w-32" />
            </div>

            {/* Content skeleton */}
            <div className="border-t border-stone-200 p-4 space-y-4">
              <div className="flex gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2">
                    <Skeleton className="h-4 w-16" />
                    <Skeleton className="h-3 w-12" />
                  </div>
                  <Skeleton className="h-16 w-full" />
                </div>
              </div>

              {/* Comment editor skeleton */}
              <div className="border-t border-stone-200 pt-4">
                <Skeleton className="h-32 w-full rounded-md" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Component-specific error fallback
function PullDiscussionError({ resetErrorBoundary }: { resetErrorBoundary: () => void }) {
  return (
    <div className="w-full py-6">
      <div className="px-4">
        <Separator className="w-full mb-6" />
        <div className="max-w-4xl mx-auto">
          <div className="border border-stone-200 rounded-lg bg-white p-8 text-center">
            <AlertTriangleIcon className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Unable to load discussion</h3>
            <p className="text-muted-foreground mb-4">
              There was an error loading the pull request discussion. Please try again.
            </p>
            <Button onClick={resetErrorBoundary} variant="outline">
              <RefreshCwIcon className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Normalization functions
function normalizeIssueComment(comment: IssueComment): NormalizedComment {
  return {
    id: comment.id ?? null,
    gid: comment.gid,
    author: {
      login: comment.authorLogin,
      id: comment.author.id,
      avatarUrl: comment.author.avatarUrl,
    },
    body: comment.body,
    createdAt: comment.createdAt,
    updatedAt: comment.updatedAt,
    type: 'issue_comment' as const,
  };
}

function normalizeReview(review: ReviewRecordRow): NormalizedComment | null {
  return review.author && review.createdAt && review.updatedAt && review.body.length > 0
    ? {
        id: review.id ?? null,
        gid: review.gid,
        author: {
          login: review.author.login,
          id: review.author.id,
          avatarUrl: review.author.avatarUrl,
        },
        body: review.body,
        createdAt: review.createdAt,
        updatedAt: review.updatedAt,
        state: review.state as string,
        type: 'review' as const,
      }
    : null;
}

export function PullDiscussion() {
  return (
    <ErrorBoundary FallbackComponent={PullDiscussionError}>
      <Suspense fallback={<PullDiscussionSkeleton />}>
        <PullDiscussionInner />
      </Suspense>
    </ErrorBoundary>
  );
}

const PullDiscussionInner = memo(() => {
  const { owner, repo } = useParams({ from: '/_authed/_layout/$owner/$repo/pull/$number' });
  const { data: collaborators } = useQuery(collaboratorsQuery(owner, repo));
  const { discussionComments, createIssueComment, isLoading } = usePullDiscussionData();

  const [isDiscussionExpanded, setIsDiscussionExpanded] = useState(true);
  const [showAllComments, setShowAllComments] = useState(false);
  const [isSingleCommentExpanded, setIsSingleCommentExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const editorRef = useRef<Editor>(null);

  // Determine which comments to show
  const commentsToShow =
    showAllComments || discussionComments.length <= 1 ? discussionComments : [discussionComments[0]];
  const hasMoreComments = discussionComments.length > 1 && !showAllComments;
  const isSingleComment = discussionComments.length === 1;

  const handleSubmitComment = () => {
    createIssueComment(comment);
    setComment('');
    editorRef.current?.commands.clearContent();
  };

  if (discussionComments.length === 0) {
    return (
      <EmptyDiscussion
        isExpanded={isDiscussionExpanded}
        onToggle={() => setIsDiscussionExpanded(!isDiscussionExpanded)}
        editorRef={editorRef}
        comment={comment}
        onCommentChange={setComment}
        onSubmit={handleSubmitComment}
        collaborators={collaborators}
      />
    );
  }

  return (
    <div className="w-full py-6" data-section-name="Discussion">
      <div className="px-4">
        <Separator className="w-full mb-6" />
        <div className="max-w-4xl mx-auto">
          {/* Discussion Card */}
          <div className="border border-stone-200 overflow-hidden rounded-lg bg-white">
            {/* Discussion Header */}
            <DiscussionHeader
              isExpanded={isDiscussionExpanded}
              onToggle={() => setIsDiscussionExpanded(!isDiscussionExpanded)}
              lastActivity={discussionComments[discussionComments.length - 1]?.createdAt}
              commentCount={discussionComments.length}
            />

            {/* Discussion Content */}
            {isDiscussionExpanded && (
              <div className="border-t border-stone-200">
                <CommentsList
                  owner={owner}
                  comments={commentsToShow}
                  allComments={discussionComments}
                  showAllComments={showAllComments}
                  hasMoreComments={hasMoreComments}
                  isSingleComment={isSingleComment}
                  isSingleCommentExpanded={isSingleCommentExpanded}
                  onToggleShowAll={() => setShowAllComments(!showAllComments)}
                  onToggleSingleComment={() => setIsSingleCommentExpanded(!isSingleCommentExpanded)}
                />

                {/* Comment Editor inside the card */}
                <div className="border-t border-stone-200 p-4">
                  <DiscussionCommentEditor
                    owner={owner}
                    ref={editorRef}
                    comment={comment}
                    onCommentChange={setComment}
                    replyQuote={undefined}
                    clearReplyQuote={() => {}}
                    onSubmit={handleSubmitComment}
                    mentionCollaborators={collaborators}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

// Sub-components
const DiscussionHeader = memo(
  ({
    isExpanded,
    onToggle,
    lastActivity,
    commentCount,
  }: {
    isExpanded: boolean;
    onToggle: () => void;
    lastActivity?: string;
    commentCount: number;
  }) => (
    <div
      className="flex items-center justify-between p-4 cursor-pointer hover:bg-stone-50 transition-colors"
      onClick={onToggle}
    >
      <div className="flex items-center gap-3">
        <ChevronRightIcon
          className={cn('size-4 text-stone-600 transition-transform duration-200', isExpanded && 'rotate-90')}
        />
        <h3 className="text-base font-medium">Discussion</h3>
      </div>
      <div className="flex items-center gap-2">
        {lastActivity && <span className="text-sm text-stone-500">Last activity {getTimeAgo(lastActivity)}</span>}
      </div>
    </div>
  )
);

const EmptyDiscussion = memo(
  ({ isExpanded, onToggle, editorRef, comment, onCommentChange, onSubmit, collaborators, owner }: any) => (
    <div className="w-full py-6">
      <div className="px-4">
        <Separator className="w-full mb-6" />
        <div className="max-w-4xl mx-auto">
          <div className="border border-stone-200 rounded-lg bg-white">
            <DiscussionHeader isExpanded={isExpanded} onToggle={onToggle} commentCount={0} />

            {isExpanded && (
              <div className="border-t border-stone-200">
                <div className="flex flex-col items-center justify-center py-6 text-center">
                  <div className="rounded-full bg-stone-200 p-3 mb-3">
                    <MessageSquareDashedIcon className="size-6 text-stone-600" />
                  </div>
                  <h3 className="text-base font-medium mb-1">No comments yet</h3>
                  <p className="text-sm text-stone-500 max-w-md mb-4">
                    Start the conversation by adding a comment about this pull request.
                  </p>
                </div>

                <div className="border-t border-stone-200 p-4">
                  <DiscussionCommentEditor
                    owner={owner}
                    ref={editorRef}
                    comment={comment}
                    onCommentChange={onCommentChange}
                    replyQuote={undefined}
                    clearReplyQuote={() => {}}
                    onSubmit={onSubmit}
                    mentionCollaborators={collaborators}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
);

const CommentsList = memo(
  ({
    comments,
    allComments,
    showAllComments,
    hasMoreComments,
    isSingleComment,
    isSingleCommentExpanded,
    onToggleShowAll,
    onToggleSingleComment,
    owner,
  }: {
    comments: NormalizedComment[];
    allComments: NormalizedComment[];
    showAllComments: boolean;
    hasMoreComments: boolean;
    isSingleComment: boolean;
    isSingleCommentExpanded: boolean;
    onToggleShowAll: () => void;
    onToggleSingleComment: () => void;
    owner: string;
  }) => {
    const shouldTruncate = !showAllComments && !isSingleCommentExpanded && (hasMoreComments || isSingleComment);

    return (
      <div className="relative">
        <div className={cn('p-4 space-y-4', shouldTruncate && 'max-h-48 overflow-hidden')}>
          {comments.map((comment, index) => (
            <div key={comment.type === 'issue_comment' ? comment.gid : comment.id}>
              <IssueCommentItem comment={comment} owner={owner} />
              {index < comments.length - 1 && <Separator className="mt-4" />}
            </div>
          ))}

          {showAllComments && allComments.length > 1 && (
            <div className="flex justify-center pt-4">
              <button
                onClick={onToggleShowAll}
                className="flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded-md transition-colors cursor-pointer"
              >
                <ChevronUpIcon className="size-4" />
                Show less
              </button>
            </div>
          )}

          {hasMoreComments && (
            <div className="relative">
              <div className="relative overflow-hidden">
                <div className="opacity-50">
                  <Separator className="mt-4 mb-4" />
                  <div className="truncate">
                    <IssueCommentItem comment={allComments[1]} owner={owner} />
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white pointer-events-none" />
              </div>

              <div className="flex justify-center pt-2">
                <button
                  onClick={onToggleShowAll}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded-md transition-colors cursor-pointer"
                >
                  <ChevronDownIcon className="size-4" />
                  Show {allComments.length - 1} more comment{allComments.length - 1 !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Show expand/collapse button for single long comment or multiple comments when truncated */}
        {shouldTruncate && (
          <>
            <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none" />
            <div className="absolute bottom-2 left-0 right-0 flex justify-center">
              <button
                onClick={isSingleComment ? onToggleSingleComment : onToggleShowAll}
                className="flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:text-stone-800 bg-white hover:bg-stone-100 rounded-md transition-colors shadow-sm border border-stone-200 cursor-pointer"
              >
                <ChevronDownIcon className="size-4" />
                {isSingleComment
                  ? 'Show full comment'
                  : allComments.length > 1
                    ? `Show ${allComments.length - 1} more comment${allComments.length - 1 !== 1 ? 's' : ''}`
                    : 'Show more'}
              </button>
            </div>
          </>
        )}

        {/* Show collapse button when single comment is expanded */}
        {isSingleComment && isSingleCommentExpanded && (
          <div className="flex justify-center pt-4">
            <button
              onClick={onToggleSingleComment}
              className="flex items-center gap-2 px-3 py-2 text-sm text-stone-600 hover:text-stone-800 hover:bg-stone-100 rounded-md transition-colors cursor-pointer"
            >
              <ChevronUpIcon className="size-4" />
              Show less
            </button>
          </div>
        )}
      </div>
    );
  }
);

const IssueCommentItem = memo(({ comment, owner }: { comment: NormalizedComment; owner: string }) => {
  return (
    <div className="flex gap-3 select-text">
      <div className="shrink-0">
        <Avatar className="size-8">
          <AvatarImage src={comment.author.avatarUrl} alt={comment.author.login} />
          <AvatarFallback>
            <UserIcon className="size-4" />
          </AvatarFallback>
        </Avatar>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-semibold">{comment.author.login.replace(/\[bot\]/, '')}</span>
          {comment.author.login.endsWith('[bot]') && (
            <div className="text-xs px-2 text-muted-foreground border border-stone-300 rounded-full">Bot</div>
          )}
          <span className="text-xs text-gray-500">{getTimeAgo(comment.createdAt)}</span>
        </div>

        {comment.state === 'APPROVED' && (
          <Badge className="flex items-center gap-2 text-xs bg-emerald-600 mb-2">
            <ThumbsUpIcon className="size-4 text-white" />
            <span className="text-xs text-white">Approved this pull request</span>
          </Badge>
        )}
        {comment.state === 'CHANGES_REQUESTED' && (
          <Badge className="flex items-center gap-2 text-xs bg-red-600 mb-2">
            <ThumbsDownIcon className="size-4 text-white" />
            <span className="text-xs text-white">Requested changes</span>
          </Badge>
        )}

        <MarkdownEditor value={comment.body ?? ''} editable={false} owner={owner} />

        {/* Reactions */}
        {comment.id && (
          <ReactionBar
            subjectId={comment.id}
            commentType={comment.type === 'issue_comment' ? 'issue' : 'review_body'}
            className="mt-2"
          />
        )}
      </div>
    </div>
  );
});
